/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { ChevronDown, Search, Server } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import config from "../config";

// ✅ Flag renderer with emoji fallback + rounded style
function CountryFlag({ code, name, size = 20 }) {
  if (!code) {
    return (
      <div
        className="w-5 h-5 flex items-center justify-center bg-gray-200 rounded"
        aria-hidden
        title={name || "Unknown"}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" stroke="#cbd5e1" strokeWidth="1.5" />
        </svg>
      </div>
    );
  }

  return (
    <ReactCountryFlag
      countryCode={code}
      svg
      title={name}
      fallback={() => (
        <span
          style={{
            fontSize: `${size}px`,
            lineHeight: 1,
            display: "inline-block",
          }}
          role="img"
          aria-label={name}
        >
          {String.fromCodePoint(
            ...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt()),
          )}
        </span>
      )}
      style={{
        width: size,
        height: size,
        objectFit: "cover",
        borderRadius: "4px",
      }}
    />
  );
}

export default function BuyNumbersPage() {
  const [price, setPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [orderProcessing, setOrderProcessing] = useState(false); // ✅ New state for order processing
  const [serviceList, setServiceList] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedPool, setSelectedPool] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const countryDropdownRef = useRef(null);
  const serviceDropdownRef = useRef(null);
  const navigate = useNavigate();

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()),
  );

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchCountries`,
        );
        const data = res.data;

        if (!Array.isArray(data)) {
          console.warn("Expected an array of countries but got:", data);
          setCountries([]);
          return;
        }

        const mapped = data.map((c) => ({
          name: c.name,
          code: c.short_name,
          slug: c.id?.toString() || c.name.toLowerCase(),
        }));

        mapped.sort((a, b) => a.name.localeCompare(b.name));
        setCountries(mapped);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setCountries([]);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchServices`,
        );

        const data = res.data;
        if (!Array.isArray(data)) {
          console.warn("Expected an array of services but got:", data);
          setServiceList([]);
          return;
        }

        // Keep both id and name
        const mapped = data.map((s) => ({
          id: s.id,
          name: s.name,
        }));

        mapped.sort((a, b) => a.name.localeCompare(b.name));
        setServiceList(mapped);
      } catch (error) {
        console.error("❌ Error fetching services:", error);
        setServiceList([]);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }

      if (
        serviceDropdownRef.current &&
        !serviceDropdownRef.current.contains(event.target)
      ) {
        setShowServiceDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedCountry && selectedService) {
      fetchSuggestedPools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, selectedService]);

  // ✅ Fetch suggested pools
  const fetchSuggestedPools = async () => {
    if (!selectedService || !selectedCountry) return;

    try {
      setPriceLoading(true);

      const res = await axios.post(
        `${config.API_BASE_URL}/api/auth/fetchSuggestedPools`,
        {
          country: selectedCountry?.code,
          service: selectedService?.id || selectedService?.name,
          serviceName: selectedService?.name,
        },
      );

      const data = res.data;

      if (!data?.pools || data.pools.length === 0 || data.stock === 0) {
        toast.error("⚠️ This service is not available right now.");
        setPrice(null);
        setSelectedPool(null);
        return;
      }

      // ✅ Find the cheapest pool
      const cheapestPool = data.pools.reduce((prev, curr) =>
        curr.price < prev.price ? curr : prev,
      );

      // ✅ Save pool and price
      setSelectedPool(cheapestPool);
      setPrice(cheapestPool.price);
    } catch (err) {
      console.error("Error fetching price:", err.response?.data || err.message);
      toast.error("❌ Failed to fetch price. Try again later.");
      setPrice(null);
      setSelectedPool(null);
    } finally {
      setPriceLoading(false);
    }
  };

  const createOrder = async (
    selectedCountry,
    selectedService,
    selectedPool,
    price,
  ) => {
    if (!selectedCountry || !selectedService || !selectedPool || !price) {
      toast.error("⚠️ Missing required order details.");
      return;
    }

    // ✅ Set processing state to true - this disables the button
    setOrderProcessing(true);

    try {
      toast.loading("📦 Creating order...");

      const token = localStorage.getItem("authToken");

      const res = await axios.post(
        `${config.API_BASE_URL}/api/auth/buy-activation-number`,
        {
          country: selectedCountry?.code,
          serviceId: selectedService?.id,
          serviceName: selectedService?.name,
          pool: selectedPool,
          price,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          // withCredentials: true, // Remove this since we're using Bearer token
        },
      );

      toast.dismiss();

      if (res.data.success) {
        const { data } = res.data;

        // ✅ Success toast
        toast.success(
          `🎉 Order created successfully! Phone: ${data.phoneNumber}`,
        );

        // ⏳ Auto navigate after 1 second (button stays disabled during this time)
        setTimeout(() => {
          navigate("/dashboard/SmsOrder");
        }, 1000);
      } else {
        toast.error(res.data.message || "❌ Failed to create order.");
        // ✅ Only re-enable button if there's an error
        setOrderProcessing(false);
      }
    } catch (err) {
      toast.dismiss();
      console.error(
        "❌ Error creating order:",
        err.response?.data || err.message,
      );

      // Check for specific error types
      if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("authToken");
        navigate("/login");
      } else if (err.response?.status === 400) {
        toast.error(err.response.data?.message || "Invalid request.");
      } else {
        toast.error("❌ Failed to create order. Try again later.");
      }

      // ✅ Re-enable button on error so user can try again
      setOrderProcessing(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* <ProtectDashboardRoute /> */}
      <Toaster position="top-center" />
      <Sidebar />
      <MobileHeader />

      <div className="flex-1 flex flex-col mt-[70px] lg:mt-0">
        <main className="p-4 md:p-8 flex-1 w-full max-w-full overflow-x-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 md:mb-8">
            Recieve Global SMS
          </h1>

          {/* Country Input Dropdown */}
          <div ref={countryDropdownRef} className="mb-6 relative mt-[20px]">
            <h2 className="text-lg font-semibold mb-2">Select Country</h2>

            <div
              className={`relative bg-white border rounded-lg px-3 py-2 md:px-4 md:py-3 flex justify-between items-center w-full transition ${
                priceLoading || orderProcessing
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:border-gray-400"
              }`}
              onClick={() =>
                !priceLoading &&
                !orderProcessing &&
                setShowCountryDropdown(!showCountryDropdown)
              }
            >
              <span className="flex items-center gap-2">
                {selectedCountry && (
                  <CountryFlag
                    code={selectedCountry.code}
                    name={selectedCountry.name}
                    size={20}
                  />
                )}
                <span>
                  {selectedCountry
                    ? selectedCountry.name
                    : "Choose a country..."}
                </span>
              </span>
              <ChevronDown className="w-5 h-5 text-gray-500" />
            </div>

            {showCountryDropdown && !priceLoading && !orderProcessing && (
              <div className="absolute mt-2 bg-white border rounded-lg shadow-lg w-full z-50">
                <div className="flex items-center bg-gray-100 rounded-t-lg px-3 py-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    className="bg-transparent outline-none px-2 w-full text-gray-700"
                  />
                </div>

                <ul className="max-h-60 overflow-y-auto">
                  {filteredCountries.map((country) => (
                    <li
                      key={country.code + country.name}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm md:text-base transition"
                      onClick={() => {
                        setSelectedCountry(country);
                        setShowCountryDropdown(false);
                      }}
                    >
                      <CountryFlag
                        code={country.code}
                        name={country.name}
                        size={18}
                      />
                      <span>{country.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Service Input Dropdown */}
          <div ref={serviceDropdownRef} className="mb-6 relative mt-[20px]">
            <h2 className="text-lg font-semibold mb-2">Select Service</h2>

            <div
              className={`relative bg-white border rounded-lg px-3 py-2 md:px-4 md:py-3 flex justify-between items-center w-full transition ${
                priceLoading || orderProcessing
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:border-gray-400"
              }`}
              onClick={() =>
                !priceLoading &&
                !orderProcessing &&
                setShowServiceDropdown(!showServiceDropdown)
              }
            >
              <span className="flex items-center gap-2">
                <span>
                  {selectedService?.name
                    ? selectedService.name
                    : "Choose a service..."}
                </span>
              </span>
              <ChevronDown className="w-5 h-5 text-gray-500" />
            </div>

            {showServiceDropdown && !priceLoading && !orderProcessing && (
              <div className="absolute mt-2 bg-white border rounded-lg shadow-lg w-full z-50">
                <div className="flex items-center bg-gray-100 rounded-t-lg px-3 py-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    className="bg-transparent outline-none px-2 w-full text-gray-700"
                  />
                </div>

                <ul className="max-h-60 overflow-y-auto">
                  {serviceList
                    .filter((s) =>
                      s.name
                        .toLowerCase()
                        .includes(serviceSearch.toLowerCase()),
                    )
                    .map((service, index) => (
                      <li
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm md:text-base transition"
                        onClick={() => {
                          setSelectedService(service);
                          setShowServiceDropdown(false);
                          fetchSuggestedPools(); // ✅ Auto-fetch when service selected
                        }}
                      >
                        <Server className="w-4 h-4 text-blue-500" />
                        <span>{service.name}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>

          {/* Purchase Button — Only visible when price is available */}
          {selectedService && selectedCountry && (
            <div className="mt-6">
              {price !== null && (
                <button
                  disabled={!price || priceLoading || orderProcessing}
                  onClick={() =>
                    createOrder(
                      selectedCountry,
                      selectedService,
                      selectedPool?.pool, // ✅ send the pool ID (e.g. 7)
                      price,
                    )
                  }
                  className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold shadow transition flex items-center justify-center gap-2 ${
                    priceLoading || !price || orderProcessing
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-[#007AFF] hover:bg-[#005FCC] text-white"
                  }`}
                >
                  {orderProcessing ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing Order...
                    </>
                  ) : priceLoading ? (
                    "Fetching Pools..."
                  ) : price ? (
                    `Purchase ₦${price}`
                  ) : (
                    "Unavailable Select options to continue"
                  )}
                </button>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
